import { Fade, Flex, Hide } from '@chakra-ui/react';
import { Paragraph } from 'ui-components';
import { Button } from 'ui-components/dist/Button';
import authApi from '~/authApi';
import { FoamLogo } from '~/components/icons';
import { FEATURE_FLAGS } from '~/constants';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';
import { useScrollDirection } from '~/hooks/useScrollDirection';
import { convertPxToRems } from '~/utils-ts';

interface MarketingBannerProps {
  agencyName?: string;
  entity: SHARED_ENTITIES;
  trackId?: string;
  hide?: boolean;
}

export enum SHARED_ENTITIES {
  roster = 'roster',
  playlist = 'playlist',
  mediaPack = 'media pack',
  content = 'content',
}

export const MarketingBanner: React.FC<MarketingBannerProps> = ({
  agencyName,
  entity,
  hide = false,
  ...props
}) => {
  const { isEnabled: isFoamMarketingBannerFFEnabled } = useFeature(
    FEATURE_FLAGS.foamMarketingBanner,
  );
  const isLoggedinUser = !!authApi.user?.id;

  const { hasScrolled, isScrolleable } = useScrollDirection();

  const shouldRenderBanner =
    !isLoggedinUser && isFoamMarketingBannerFFEnabled && !hide;

  if (!shouldRenderBanner) return null;

  const paragraphText = `${agencyName} shared this ${entity} using Foam. ${
    entity === SHARED_ENTITIES.content
      ? 'Share content'
      : `Create your own ${entity}`
  } on Foam for free.`;

  return (
    <Fade in={!hasScrolled}>
      <Flex
        position="fixed"
        borderColor="gray.400"
        backgroundColor="primary.0"
        boxShadow="marketingBanner"
        borderRadius="2xl"
        gap={3}
        marginX="auto"
        left={0}
        right={0}
        bottom={isScrolleable ? 8 : convertPxToRems(112)}
        padding={2}
        width={{
          sm: convertPxToRems(533),
          base: convertPxToRems(304),
        }}
        minHeight={{
          sm: convertPxToRems(56),
          base: convertPxToRems(103),
        }}
        alignItems={{
          sm: 'center',
          base: 'start',
        }}
        flexDirection={{
          sm: 'row',
          base: 'column',
        }}
        {...props}
      >
        <Hide below="sm">
          <FoamLogo width={convertPxToRems(40)} height={convertPxToRems(40)} />
        </Hide>

        <Paragraph size="md" color="primary.700" fontWeight="medium">
          {paragraphText}
        </Paragraph>
        <Button
          size="sm"
          sx={{
            whiteSpace: 'nowrap',
            wordWrap: 'normal',
          }}
          href={`https://${process.env.NEXT_PUBLIC_SHARED_PROFILES_HOST}`}
          target="_blank"
          as="a"
          rel="noopener noreferrer"
          trackId="foam-marketing-banner-learn-more"
        >
          <Paragraph
            size="md"
            color="primary.0"
            paddingX={{
              base: 0,
              sm: 4,
            }}
            sx={{
              lineHeight: 1,
            }}
          >
            Learn more
          </Paragraph>
        </Button>
      </Flex>
    </Fade>
  );
};
